import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  CategoryTree,
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import {
  findCategoryById,
  formatCategoryText,
} from '@/lib/utils/category-utils';
import { createSubRowSortingFn } from '@/lib/utils/table-filter-utils';
import {
  isDealItemCategoryUpdating,
  resolveDealItemFromMap,
} from '@/lib/utils/updating-fields';
import { categoryTreeState } from '@/states/category-tree-map.state';
import { useSetColumnSort } from '@/states/column-sort.state';
import { productsMapState } from '@/states/products.state';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';

import { ProductCategoryCell } from '../cells/product-category-cell';
import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  dealId: string;
  filterFn?: (
    row: Row<DealItemWithVersions>,
    columnId: string,
    filterValue: unknown
  ) => boolean;
}

type CategoryCellProps = {
  row: Row<DealItemWithVersions>;
  dealId: string;
  isOfferLocked: boolean;
  categoryTree: CategoryTree[];
  displayValue: string;
  productCategoryId: string | null;
  isEmpty: boolean;
  isParent: boolean;
};

function CategoryCell({
  row,
  dealId,
  isOfferLocked,
  categoryTree,
  displayValue,
  productCategoryId,
  isEmpty,
  isParent,
}: CategoryCellProps) {
  const productsMap = useAtomValue(productsMapState);
  const isLoading = isDealItemCategoryUpdating(
    resolveDealItemFromMap(productsMap, row.original)
  );
  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="productCategory"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <ProductCategoryCell
          isLoading={isLoading}
          dealId={dealId}
          dealItemId={row.original.id}
          productCategoryId={productCategoryId}
          isOfferLocked={isOfferLocked}
          isParent={isParent}
          displayValue={displayValue}
          isEmpty={isEmpty}
          categoryTree={categoryTree}
          isRecentlyModified={isRecentlyModified}
        />
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefType = ({
  dealId,
  filterFn,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const isOfferLocked = useIsOfferLocked();
  const { setColumnSort } = useSetColumnSort();
  // Read categoryTree once for sorting function (sorting happens on initial load)
  const categoryTreeForSorting = useAtomValue(categoryTreeState);

  const accessorKey: keyof DealItem = 'productCategory';

  const getValue = (
    row: Row<DealItemWithVersions>,
    categoryTree: CategoryTree[]
  ) => {
    const categoryId =
      row.original.activeVersion.productCategory?.productCategoryId ?? null;
    if (!categoryId) return '—';
    const category = findCategoryById(categoryTree, categoryId);
    return category ? formatCategoryText(category.label) : '—';
  };

  const getParentValue = (
    row: Row<DealItemWithVersions>,
    categoryTree: CategoryTree[]
  ) => {
    const subRows = row.subRows;
    if (!subRows || subRows.length === 0) {
      return '—';
    }

    const firstCategoryId =
      subRows[0]?.original.activeVersion.productCategory?.productCategoryId;
    if (!firstCategoryId) {
      return '—';
    }

    const allSameCategory = subRows.every(
      (subRow) =>
        subRow.original.activeVersion.productCategory?.productCategoryId ===
        firstCategoryId
    );

    if (allSameCategory) {
      const category = findCategoryById(categoryTree, firstCategoryId);
      return category ? formatCategoryText(category.label) : '—';
    }

    return '≠';
  };

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'Type',
      filterFn: filterFn,
      sortingFn: createSubRowSortingFn('productCategory', (row) => {
        const categoryId =
          row.activeVersion?.productCategory?.productCategoryId;
        if (!categoryId) return null;
        const category = findCategoryById(categoryTreeForSorting, categoryId);
        return category ? formatCategoryText(category.label) : null;
      }),
      header: () => (
        <div
          className="flex items-center"
          onClick={() => setColumnSort(accessorKey)}
        >
          {t('kpi.type')} <SortIcon pointer="productCategory" />
        </div>
      ),
      cell: ({ row }) => {
        const isParent = row.subRows.length > 1;
        const productCategoryId =
          row.original.activeVersion.productCategory?.productCategoryId ?? null;

        const errorState = getTableCellErrorState({
          row,
          field: 'productCategory',
          errorType: 'empty',
        });

        const displayValue = isParent
          ? getParentValue(row, categoryTreeForSorting)
          : getValue(row, categoryTreeForSorting);

        return (
          <CategoryCell
            row={row}
            dealId={dealId}
            isOfferLocked={isOfferLocked}
            categoryTree={categoryTreeForSorting}
            displayValue={displayValue}
            productCategoryId={productCategoryId}
            isEmpty={errorState.isEmpty}
            isParent={isParent}
          />
        );
      },
      size: 180,
    }),
    [t, isOfferLocked, setColumnSort, categoryTreeForSorting, dealId, filterFn]
  );
};

export default useColumnDefType;
